import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const num = (v) => parseFloat(v) || 0;
const isOther = (d) => (d || "").trim().toLowerCase() === "other";

/**
 * Build & open the voucher-style Jobcard report PDF (in a new tab).
 *
 * @param {Object} opts
 * @param {string} opts.orderNo        Store order number
 * @param {string} opts.storeName      Shown top-right
 * @param {string} opts.karigarName    Shown under the order no
 * @param {Array}  opts.entries        Normalized rows, one per QR:
 *   { qr, grossWithLabel, labelWt, stones: [{ type, description, weight }] }
 * @param {Object} opts.quickNoByType  { [Stone_name]: Quick_no } — sorts stone columns
 */
export const generateJobcardReportPdf = ({
  orderNo = "",
  storeName = "",
  karigarName = "",
  entries = [],
  quickNoByType = {},
}) => {
  const rows = (entries || []).filter(
    (e) => e && String(e.qr ?? "").trim() !== ""
  );
  if (rows.length === 0) return;

  // Distinct stone types → columns, sorted by Quick_no (unknown last, then name).
  // A stone type only becomes a column if it has a non-zero weight in at least
  // one row — otherwise a stone that was saved with a pcs but no weight would
  // show up as an empty column.
  const stoneTypes = [];
  rows.forEach((e) =>
    (e.stones || []).forEach((s) => {
      const t = (s.type || "").trim();
      if (t && num(s.weight) > 0 && !stoneTypes.includes(t)) {
        stoneTypes.push(t);
      }
    })
  );
  stoneTypes.sort((a, b) => {
    const qa = quickNoByType[a] ?? Infinity;
    const qb = quickNoByType[b] ?? Infinity;
    if (qa !== qb) return qa - qb;
    return a.localeCompare(b);
  });

  const colTotals = {};
  stoneTypes.forEach((t) => (colTotals[t] = 0));
  let grossTotal = 0;
  let labelTotal = 0;
  let stoneGrand = 0;

  const body = rows.map((e, idx) => {
    const grossWithLabel = num(e.grossWithLabel);
    grossTotal += grossWithLabel;
    labelTotal += num(e.labelWt);

    const cell = {}; // per type: displayed weight (includes "Other")
    let totalStRow = 0; // excludes "Other"
    (e.stones || []).forEach((s) => {
      const t = (s.type || "").trim();
      const w = num(s.weight);
      if (t) cell[t] = (cell[t] || 0) + w;
      if (!isOther(s.description)) {
        totalStRow += w;
        if (t && colTotals[t] != null) colTotals[t] += w;
      }
    });
    stoneGrand += totalStRow;

    return [
      idx + 1,
      e.qr,
      "", // Item Name — blank for now
      grossWithLabel ? grossWithLabel.toFixed(3) : "",
      ...stoneTypes.map((t) => (cell[t] ? cell[t].toFixed(3) : "")),
      totalStRow ? totalStRow.toFixed(3) : "",
    ];
  });

  const netTotal = grossTotal - labelTotal - stoneGrand;
  const tagCount = rows.length;
  const perPcsWt = tagCount ? labelTotal / tagCount : 0;

  const head = [
    ["S.No", "QR No", "Item Name", "Gross Wt", ...stoneTypes, "Total St"],
  ];
  const foot = [
    [
      "TOTAL",
      "",
      "",
      grossTotal.toFixed(3),
      ...stoneTypes.map((t) => colTotals[t].toFixed(3)),
      stoneGrand.toFixed(3),
    ],
  ];

  // Adapt the page to the number of columns so 8-10 stones stay readable:
  // landscape past a few stones, A3 when very wide, and a smaller font as the
  // table grows.
  const totalCols = stoneTypes.length + 5; // S.No, QR, Item, Gross, ..., Total St
  const orientation = totalCols > 7 ? "landscape" : "portrait";
  const format = totalCols > 14 ? "a3" : "a4";
  const bodyFont = totalCols > 12 ? 6.5 : totalCols > 9 ? 7 : 8;
  const cellPad = totalCols > 9 ? 1 : 1.5;
  const doc = new jsPDF({ orientation, unit: "mm", format });
  const pageWidth = doc.internal.pageSize.getWidth();

  if (storeName) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(String(storeName), pageWidth - 10, 12, { align: "right" });
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Jobcard", pageWidth / 2, 14, { align: "center" });

  const d = new Date();
  const dateStr = `${String(d.getDate()).padStart(2, "0")}/${String(
    d.getMonth() + 1
  ).padStart(2, "0")}/${d.getFullYear()}`;

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(`Order No : ${orderNo || ""}`, 10, 24);
  if (karigarName) doc.text(`Karigar : ${karigarName}`, 10, 30);
  doc.text(`Date : ${dateStr}`, pageWidth - 10, 24, { align: "right" });

  autoTable(doc, {
    startY: karigarName ? 34 : 28,
    head,
    body,
    foot,
    tableWidth: "auto", // fit the full table within the page width
    styles: {
      fontSize: bodyFont,
      cellPadding: cellPad,
      halign: "center",
      lineWidth: 0.1,
      overflow: "linebreak",
      minCellWidth: 10,
    },
    headStyles: {
      fillColor: [80, 80, 80],
      textColor: 255,
      fontSize: bodyFont,
    },
    footStyles: { fillColor: [225, 225, 225], textColor: 20, fontStyle: "bold" },
    columnStyles: {
      0: { halign: "center", cellWidth: 10 }, // S.No
      1: { halign: "left" }, // QR No
      2: { halign: "left" }, // Item Name
    },
    margin: { left: 8, right: 8 },
  });

  let y = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(10);
  const summaryLine = (label, value) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, 10, y);
    doc.setFont("helvetica", "normal");
    doc.text(`${value} Gms`, 55, y);
    y += 6;
  };
  summaryLine("GROSS WEIGHT", grossTotal.toFixed(3));
  summaryLine("LABEL WT", labelTotal.toFixed(3));
  summaryLine("STONES WEIGHT", stoneGrand.toFixed(3));
  summaryLine("NET WEIGHT", netTotal.toFixed(3));

  let ty = doc.lastAutoTable.finalY + 10;
  const tagX = pageWidth - 90;
  doc.setFont("helvetica", "bold");
  doc.text("No Of Tag", tagX, ty);
  doc.text("Per pcs Wt", tagX + 35, ty);
  doc.text("Total Wt", tagX + 70, ty);
  ty += 6;
  doc.setFont("helvetica", "normal");
  doc.text(`${tagCount}`, tagX, ty);
  doc.text(`${perPcsWt.toFixed(3)}`, tagX + 35, ty);
  doc.text(`${labelTotal.toFixed(3)}`, tagX + 70, ty);

  const fileName = `jobcard_${String(orderNo || "order").replace(
    /[^\w-]+/g,
    "_"
  )}`;
  doc.setProperties({ title: fileName });

  // Open a preview tab AND download with the correct name. The tab is for
  // viewing; the save() controls the file name (a blob URL opened in a tab
  // lets the browser fall back to a random id, so we can't rely on it alone).
  window.open(doc.output("bloburl"), "_blank");
  doc.save(`${fileName}.pdf`);
};

// storeData.stone -> { [Stone_name]: Quick_no } for column sorting.
export const buildQuickNoByType = (stones = []) => {
  const map = {};
  (stones || []).forEach((s) => {
    if (s?.Stone_name != null && s?.Quick_no != null) {
      map[s.Stone_name] = Number(s.Quick_no);
    }
  });
  return map;
};

// qrDataStore (CompleteOrder local state) -> normalized entries.
export const entriesFromQrStore = (store = {}) =>
  Object.entries(store || {})
    .filter(([qr]) => qr && qr.trim() !== "")
    .map(([qr, e]) => {
      const p = e.pcsDetails || {};
      const grossWithLabel =
        num(p.labelWeight) > 0
          ? num(p.labelWeight)
          : num(p.grossWeight) + num(p.enterlbl);
      return {
        qr,
        grossWithLabel,
        labelWt: num(p.enterlbl),
        stones: (e.stoneList || []).map((s) => ({
          type: s.type,
          description: s.description,
          weight: s.weight,
        })),
      };
    });

// fetchCompleteDetails items[] (from backend) -> normalized entries.
export const entriesFromSavedItems = (items = []) =>
  (items || []).map((it) => {
    const grossWithLabel = num(it.gross_with_label_wt) || num(it.gross_wt);
    const labelWt = Math.max(
      0,
      num(it.gross_with_label_wt) - num(it.gross_wt)
    );
    return {
      qr: it.qr_code_no,
      grossWithLabel,
      labelWt,
      stones: (it.stones || []).map((s) => ({
        type: s.stone_type,
        description: s.description,
        weight: s.stone_wt,
      })),
    };
  });
